import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BookingService, CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, of, switchMap, tap } from 'rxjs';
import { User } from '@markant-platform/shared/user/model';
import { BookingDraft, BookingPriceRequest, Cartridge } from '@markant-platform/cartridge/model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { RegexPattern } from '@markant-platform/shared/util-helpers';
import { BookingWorkflowData, Step } from './model/booking.model';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { BackofficeCartridgeService } from '@markant-platform/backoffice/data-access';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { CartridgeBookingService } from '../services/cartridge-booking.service';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { OrganizationDetailData } from 'data-model';

type UpdateData = Pick<BookingWorkflowData, 'billingEmail' | 'billingLanguageIsoCode' | 'references'>;

@Component({
  selector: 'markant-platform-booking',
  templateUrl: './booking.component.html',
  styleUrls: ['./booking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingComponent {
  step$: BehaviorSubject<Step> = new BehaviorSubject<Step>('form');

  bookingData$: Observable<BookingWorkflowData>;
  bookingMainData!: BookingWorkflowData;

  additionalBookingDataForm: FormGroup = new FormGroup({
    billingEmail: new FormControl(null, [
      Validators.required,
      Validators.pattern(RegexPattern.BILLING_EMAIL_PATTERN),
      Validators.pattern(RegexPattern.XSS_PATTERN),
    ]),
    billingLanguageIsoCode: new FormControl(null, Validators.required),
    references: new FormGroup({
      customerCartridgeReferenceId: new FormControl(null),
      costCenter: new FormControl(null),
      otherReferences: new FormControl(null),
    }),
  });

  isAlreadyBookedDialogOpen$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private updateDataSubject = new BehaviorSubject<UpdateData>({});

  constructor(
    private organizationService: OrganizationService,
    private bookingService: BookingService,
    private cartridgesFacade: CartridgesFacade,
    private backofficeCartridgeService: BackofficeCartridgeService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private readonly cartridgeBookingService: CartridgeBookingService,
    userService: AuthenticatedUserService,
    private readonly snackbarService: SnackbarService
  ) {
    this.bookingData$ = combineLatest([
      cartridgesFacade.cartridgeById$(activatedRoute.snapshot.params['cartridgeId']),
      userService.getUser(),
      organizationService.getCurrentOrganization(),
      this.getPriceRequest(),
      this.updateDataSubject,
    ]).pipe(
      map(this.mapToBookingData.bind(this)),
      tap(result => {
        if (!result) return;
        this.bookingMainData = result;
        this.cartridgeBookingService.pushTrackingEventData('Step 00 - Click To Order', result, 'Click');
        this.cartridgeBookingService.pushTrackingEventData('Step 01 - Your data', result, 'Pageview');
      })
    );
  }

  goTo(step: Step): void {
    if (step === 'form') {
      this.cartridgeBookingService.pushTrackingEventData('Step 02 - Summary', this.bookingMainData, 'Click - Back');
    }

    this.step$.next(step);
  }

  updateDataAndGoToOverview(): void {
    // change region code to uppercase
    const billingLanguageIsoCode = this.additionalBookingDataForm
      .get('billingLanguageIsoCode')
      ?.value.split('-')
      .reduce((prev: string, cur: string, index: number, array: string[]) => {
        if (prev) {
          if (index === array.length - 1) {
            return prev + '-' + cur.toUpperCase();
          }
          return prev + '-' + cur;
        }
        return cur;
      }, '');

    this.updateDataSubject.next({
      ...this.additionalBookingDataForm.getRawValue(),
      billingLanguageIsoCode,
    });
    this.goTo('overview');
  }

  async bookVariantAndGoToConfirmation(bookingData: BookingWorkflowData): Promise<void> {
    let updatedOrganizationData: OrganizationDetailData | undefined;
    this.loading$.next(true);

    if (!bookingData.organization.billingEmail || !bookingData.organization.billingLanguageIsoCode) {
      updatedOrganizationData = await this.updateOrganization(bookingData);
    }
    await this.bookVariant(bookingData, updatedOrganizationData);

    this.cartridgeBookingService.pushTrackingEventData(
      'Step 02 - Summary',
      this.bookingMainData,
      'Click - Send order request'
    );

    this.loading$.next(false);
  }

  closeAlreadyBookedDialog(): void {
    this.isAlreadyBookedDialogOpen$.next(false);
  }

  private getPriceRequest(): Observable<BookingPriceRequest | undefined> {
    // Fetch price request data if an id is provided and otherwise return undefined
    return this.activatedRoute.snapshot.queryParams['priceRequestId']
      ? this.backofficeCartridgeService
          .getPriceRequestById(this.activatedRoute.snapshot.queryParams['priceRequestId'])
          .pipe(map(priceRequest => priceRequest as unknown as BookingPriceRequest))
      : of(undefined);
  }

  private mapToBookingData([cartridge, user, organization, priceRequest, updateData]: [
    Cartridge | undefined,
    User,
    OrganizationDetailData | undefined,
    BookingPriceRequest | undefined,
    UpdateData,
  ]): BookingWorkflowData {
    const variant = cartridge?.variants.find(
      variant => variant.variantId === this.activatedRoute.snapshot.params['variantId']
    );

    if (!cartridge || !variant || !organization) {
      return this.router.navigate(['cartridge', 'catalog']) as unknown as BookingWorkflowData;
    }

    return {
      cartridge,
      variant,
      user,
      organization,
      priceRequest,
      ...updateData,
      serviceGtcAccepted: false,
    };
  }

  private async updateOrganization(bookingData: BookingWorkflowData): Promise<OrganizationDetailData> {
    const updatedOrganization = {
      ...bookingData.organization,
      billingEmail: bookingData.organization.billingEmail || bookingData.billingEmail,
      billingLanguageIsoCode: bookingData.organization.billingLanguageIsoCode || bookingData.billingLanguageIsoCode,
    };

    try {
      return await firstValueFrom(
        this.organizationService.setOrganizationDetailsById(
          bookingData.organization.id as string,
          bookingData.organization,
          updatedOrganization
        )
      );
    } catch (error: unknown) {
      const httpErrorResponse = error as HttpErrorResponse;
      return await firstValueFrom(this.retryOrganizationUpdateForBooking(httpErrorResponse, bookingData));
    }
  }

  private async bookVariant(
    bookingData: BookingWorkflowData,
    updatedOrganizationData: OrganizationDetailData | undefined
  ): Promise<void> {
    const bookingDraft: BookingDraft = {
      cartridgeVariantId: bookingData.variant.variantId,
      user: {
        ...bookingData.user,
        organization: {
          ...(updatedOrganizationData ?? bookingData.organization),
          organizationId: (updatedOrganizationData ?? bookingData.organization).id,
        },
      },
      references: bookingData.references,
      serviceGtcAccepted: bookingData.serviceGtcAccepted,
    };

    try {
      const bookedCartridge = await firstValueFrom(this.bookingService.bookCartridge(bookingDraft));
      this.cartridgesFacade.updateCartridgeBookingState(bookedCartridge);

      this.goTo('confirmation');
    } catch (error: unknown) {
      const httpErrorResponse = error as HttpErrorResponse;

      if (/Cartridge.*already booked/.test(httpErrorResponse.error.message)) {
        this.isAlreadyBookedDialogOpen$.next(true);
      }
    }
  }

  private retryOrganizationUpdateForBooking(
    error: HttpErrorResponse,
    bookingData: BookingWorkflowData
  ): Observable<OrganizationDetailData> {
    const isGlnDuplicatedError =
      error.status === 400 && error.error?.message?.includes('GLN') && error.error?.message?.includes('duplicated');

    if (isGlnDuplicatedError) {
      this.snackbarService.showErrorSnackbarWithTransloco('organization.gln.validation.duplicate');
      this.loading$.next(false);
    }

    if (error.status !== HttpStatusCode.Conflict) {
      throw new Error(`Error occurred while updating organization: ${error.error?.message}`);
    }

    return this.organizationService.fetchOrganization().pipe(
      map(organization => {
        // billing-mail and billing-language are only allowed to update when it is not already set
        if (
          (organization?.billingEmail && organization?.billingEmail !== bookingData.billingEmail) ||
          (organization?.billingLanguageIsoCode &&
            organization?.billingLanguageIsoCode !== bookingData.billingLanguageIsoCode)
        ) {
          throw new Error(`Error occurred while updating organization: ${error.error?.message}`);
        }

        const updatedOrganization = {
          ...organization,
          billingEmail: bookingData.billingEmail,
          billingLanguageIsoCode: bookingData.billingLanguageIsoCode,
        } as OrganizationDetailData;

        return {
          initialOrganization: organization,
          updatedOrganization,
        };
      }),
      switchMap(organizationDetails => {
        return this.organizationService.setOrganizationDetailsById(
          organizationDetails.initialOrganization.id as string,
          organizationDetails.initialOrganization,
          organizationDetails.updatedOrganization
        );
      })
    );
  }

  async goBack(cartridgeId: string, priceRequestId?: string): Promise<void> {
    if (priceRequestId) {
      await this.router.navigate([CartridgeBookingService.ORGANIZATION_PRICE_REQUEST_URL + priceRequestId]);
    } else {
      await this.router.navigate(['/cartridge', 'catalog', cartridgeId, 'detail']);
    }
  }
}
